import React from 'react';
import { useFormContext } from 'react-hook-form';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import { CollaboratorFormModal } from '@/features/Collaborators/Edit/Form/Modais';
import { Collaborator, collaboratorSchemaHelpers } from '@/models';
import { useUpdateCollaboratorMutation } from '@/features/Collaborators/Data';
import { useToast } from '@/hooks/useToast';
import { FeedbackListFieldSet } from '@/features/Collaborators/Edit/Form/Fieldsets';
import { HiringFieldset, ResponsibilitiesFieldset, BenefitsFieldset } from '@/features/Collaborators/Edit/Form/Fieldsets';
import { GiftsAndPreferencesFieldset } from '@/features/Collaborators/Edit/Form/Fieldsets';

interface DhoUpdateModalProps {
    isOpen: boolean;
    onClose: () => void;
    collaboratorId: string | null;
}

const DhoUpdateModalContent: React.FC = () => {
    const {
        control,
        setValue,
        watch,
        formState: { errors, isSubmitting }
    } = useFormContext<Collaborator>();

    return (
        <div className="space-y-6">
            {/* ResponsibilitiesFieldset - Responsáveis */}
            <div className="-mt-6">
                <ResponsibilitiesFieldset
                    context="DHO_UPDATE"
                    control={control}
                    setValue={setValue}
                    watch={watch}
                    errors={errors}
                    isSubmitting={isSubmitting}
                    layout="full"
                />
            </div>

            {/* GiftsAndPreferencesFieldset - Interesses e Brindes */}
            <GiftsAndPreferencesFieldset
                context="DHO_UPDATE"
                control={control}
            />

            {/* FeedbackListFieldSet - Feedbacks e Ocorrências */}
            <FeedbackListFieldSet
                context="DHO_UPDATE"
            />
        </div>
    );
};

const DhoUpdateModal: React.FC<DhoUpdateModalProps> = ({
    isOpen,
    onClose,
    collaboratorId
}) => {
    // Hooks para mutação e toast (igual ao ProfileUpdateWizard)
    const updateCollaboratorMutation = useUpdateCollaboratorMutation();
    const { success, error } = useToast();

    const handleSubmit = async (data: Collaborator) => {
        try {
            // DEBUG: Log para verificar se salaryHistoryDeletedIds está presente
            console.log('🔍 DhoUpdateModal handleSubmit - data recebido:', {
                salaryHistoryDeletedIds: (data as any).salaryHistoryDeletedIds,
                salaryHistorySalaries: (data as any).salaryHistorySalaries,
                hasDeletedIds: !!(data as any).salaryHistoryDeletedIds?.length
            });

            // 🔄 Configurar updateStatus específico do DHO (igual ao ProfileUpdateWizard)
            const updatedData: Collaborator = {
                ...data,
                updatedStatus: {
                    ...data.updatedStatus,
                    byDHO: {
                        status: 'UPDATED'
                    }
                }
            };

            // DEBUG: Log antes do toBackendData
            const backendData = collaboratorSchemaHelpers.toBackendData(updatedData as any);
            console.log('🔍 Após toBackendData:', {
                salaryHistoryDeletedIds: (backendData as any).salaryHistoryDeletedIds,
                hasDeletedIds: !!(backendData as any).salaryHistoryDeletedIds?.length
            });

            // 🚀 Usar mutação real e toBackendData (igual ao ProfileUpdateWizard)
            await updateCollaboratorMutation.mutateAsync(backendData);

            // ✅ Toast de sucesso (igual ao ProfileUpdateWizard)
            success('Dados salvos!', 'Informações do DHO atualizadas com sucesso');

        } catch (submitError) {
            console.error('💥 ERRO NO SUBMIT DHO:', submitError);
            const errorMessage = submitError instanceof Error ? submitError.message : 'Erro desconhecido';
            error('Erro ao salvar dados', errorMessage);
            throw submitError;
        }
    };

    if (!isOpen || !collaboratorId) return null;

    // Fieldsets ativos no DHO_UPDATE
    const currentFieldsets = [
        'ResponsibilitiesFieldSet',
        'GiftsAndPreferencesFieldSet',
        'FeedbackListFieldSet'
    ];

    return (
        <CollaboratorFormModal
            isOpen={isOpen}
            onClose={onClose}
            context="DHO_UPDATE"
            collaboratorId={collaboratorId}
            onSubmit={handleSubmit}
            title="Atualização DHO"
            currentFieldsets={currentFieldsets}
        >
            <DhoUpdateModalContent />
        </CollaboratorFormModal>
    );
};

export default DhoUpdateModal; 